import React from 'react';
import PropTypes from 'prop-types';

import NumberInput from 'app/components/common/number-input';
import CritOpts    from 'app/components/company-admin/grant-system/rubric/crit-opts';
import helpers     from 'app/helpers/rubrics';

const {formatWeight, OptionStyles} = helpers;

/**
 *  Rubric
 *    - id
 *    - crits[]
 *      - id
 *      - label
 *      - weight (percent to 3 dec places)
 *      - range
 *        - min
 *        - max
 *      - opts[]
 *        - id
 *        - label
 *        - value
 *        - style
 *    - normType
 *    - gradLbls
 *      - id
 *      - label
 *      - style
 *      - thresold
 * 
 *  Score
 *    - absolute
 *    - percent
 *    - weighted
 *    - label
 *    - style
 *    - crits{critId:{}}
 *      - value
 *      - label
 *      - style
 *      - max
 *      - weight
 */

const buildOptAnswer = (sortIndex, crit, opt) => {
  if (!opt) return null;
  return {
    title: crit.label,
    sortIndex,
    value: opt.value,
    label: opt.label,
    style: opt.style,
    max: crit.opts[crit.opts.length - 1].value,
    weight: crit.weight,
  };
};

const buildRangeAnswer = (sortIndex, crit, value=0) => {
  return {
    title: crit.label,
    sortIndex,
    value: value,
    max: crit.range.max,
    weight: crit.weight,
  };
};

const noop = () => {};

class RubricForm extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      answers: {},
    };

    this.onChangeOpt = this.onChangeOpt.bind(this);
  }

  get result() {
    const crits = this.props.rubric.crits || [];
    let isComplete = true;
    let scoreAbsolute = 0;
    let scorePercent = 0;
    let scoreWeighted = 0;
    crits.forEach((crit) => {
      const maxVal = crit.opts?.length ? (crit.opts[crit.opts.length - 1].value) : (crit.range?.max || 0);
      const multiplier = crit.weight / 100_000;
      const answer = this.state.answers[crit.id];
      const value = answer?.value || 0;
      const weightedValue = value * multiplier;
      const percentValue = (maxVal === 0) ? 0 : ((value / maxVal) * multiplier);
      if (!answer) isComplete = false;
      scoreAbsolute += value;
      scorePercent += percentValue;
      scoreWeighted += weightedValue;
    });
    scorePercent = Math.round(scorePercent * 100_000);
    const gradLbl = (this.props.rubric.gradLbls || []).find((gradLbl) => {
      return scorePercent >= gradLbl.threshold;
    });
    scoreWeighted = Math.round(scoreWeighted * 1000);
    const score = {absolute: scoreAbsolute, percent: scorePercent, weighted: scoreWeighted, crits: this.state.answers};
    if (gradLbl) {
      score.label = gradLbl.label;
      score.style = gradLbl.style;
    } else if (score.crits?.length === 1) {
      const first = score.crits[0];
      score.label = first.label;
      score.style = first.style;
    }
    return {score, isComplete};
  }

  emitChange() {
    const {onChange} = this.props;
    if (!onChange) return;
    const {isComplete, score} = this.result;
    onChange(isComplete, score);
  }

  onChangeOpt(i, crit, opt) {
    this.setState((prevState) => {
      const answers = {...prevState.answers};
      answers[crit.id] = buildOptAnswer(i, crit, opt);
      return {answers};
    }, this.emitChange);
  }

  onChangeNumber(i, crit, value) {
    this.setState((prevState) => {
      const answers = {...prevState.answers};
      answers[crit.id] = buildRangeAnswer(i, crit, (value || 0));
      return {answers};
    }, this.emitChange);
  }

  renderScore() {
    const {rubric} = this.props;
    if (!rubric) return null;
    const hasMany = rubric.crits.length > 1;
    const {score} = this.result;
    if (!hasMany) return null;

    const fmtPercent = helpers.formatWeight(score.percent, 1);
    const hasLabel = !!(score.label && score.style);
    const questionCount = rubric.crits.length;
    const answerCount = Object.values(score.crits).filter(v => v != null).length;
    const subtitle = `${answerCount} / ${questionCount} answers`;

    return (
      <div className="rubric-form-result">
        <div>
          <div className="rubric-form-result-title">Your Weighted Score</div>
          <div className="rubric-form-result-subtitle">{subtitle}</div>
        </div>

        <CritOpts.Single
          label={hasLabel ? score.label : fmtPercent}
          value={hasLabel ? fmtPercent : null}
          style={hasLabel ? score.style : OptionStyles.BLUE}
          size="regular"
          selected
        />
      </div>
    );
  }

  render() {
    const {rubric} = this.props;
    if (!rubric) return null;
    const {crits} = rubric;
    const hasMany = crits.length > 1;

    return (
      <div className="rubric-form">
        <div className="rubric-form-crits">
          {crits.map((crit, i) => {
            const answer = this.state.answers[crit.id] || null;
            const answerValue = answer ? answer.value : null;
            return (
              <div className="rubric-form-crit" key={crit.id}>
                <div className="rubric-form-crit-head">
                  <h3 className="rubric-form-crit-head-label">{crit.label}</h3>
                  {hasMany && (
                    <div className="rubric-form-crit-head-details">
                      {`${formatWeight(crit.weight)} weight ${(answerValue == null) ? '' : (' • ' + answerValue)}`}
                    </div>
                  )}
                </div>

                <div className="rubric-form-crit-input">
                  {!!crit.opts?.length && (
                    <CritOpts crit={crit} options={crit.opts} onChange={this.onChangeOpt.bind(this, i)} value={answerValue} size="small" flex />
                  )}
                  {!!crit.range && (
                    <NumberInput value={answerValue} onChange={this.onChangeNumber.bind(this, i, crit)} name="number" label={`MIN: ${crit.range.min} | MAX: ${crit.range.max}`} min={crit.range.min} max={crit.range.max} allowNull={false} />
                  )}
                </div>
                {/* <pre>{JSON.stringify({answer},null,4)}</pre> */}
              </div>
            );
          })}
        </div>
        {hasMany && this.renderScore()}
        {/* <pre>{JSON.stringify({result: this.result},null,4)}</pre> */}
      </div>
    );
  }

}

RubricForm.propTypes = {
  rubric: PropTypes.object.isRequired,
  onChange: PropTypes.func,
};

export default RubricForm;
